import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, CalendarDays, MapPin, Plus, Search, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState, PageHeader, Pager, StatusBadge } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { OT_TONE, clp, fmtDate } from "@/lib/format";

const OtCard = ({ o }) => {
  const overdue = o.end_date < new Date().toISOString().slice(0, 10) && !["CERRADA", "CANCELADA"].includes(o.status);
  return (
    <Link to={`/gerencia/ot/${o.id}`} data-testid={`ot-card-${o.id}`} className="syc-card p-5 flex flex-col gap-3 hover:-translate-y-0.5 hover:border-[#258F00] transition-[transform,border-color]">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-xs font-bold text-[#258F00]">{o.code}</span>
        <StatusBadge status={OT_TONE[o.status] || "info"} label={o.status} />
      </div>
      <div><div className="font-bold leading-snug">{o.title}</div><div className="text-xs text-[#5b6656]">{o.client_name} · {o.project}</div></div>
      <div className="text-xs text-[#5b6656] space-y-1">
        <div className="flex items-center gap-1.5"><MapPin size={12} />{o.location || o.address || "—"}</div>
        <div className="flex items-center gap-1.5"><CalendarDays size={12} />{fmtDate(o.start_date)} → {fmtDate(o.end_date)}</div>
        <div className="flex items-center gap-1.5"><UserRound size={12} />{o.manager_name}</div>
      </div>
      <div className="mt-auto flex items-center justify-between text-sm">
        {o.can_see_economics ? <span className="font-semibold tabular-nums">{clp(o.awarded_value)}</span> : <span />}
        {overdue && <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#b42318]"><AlertTriangle size={12} />Atrasada</span>}
      </div>
    </Link>
  );
};

export default function Ots() {
  const { can } = useAuth();
  const [params] = useSearchParams();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 24 });
  useEffect(() => {
    api.get("/gerencia/ots", { params: { q, page, page_size: 24, status: params.get("status") || "", client_id: params.get("client_id") || "", overdue: params.get("overdue") === "1" } }).then((r) => setData(r.data));
  }, [q, page, params]);

  return (
    <div data-testid="ots-page">
      <PageHeader eyebrow="Gerencia" title="Órdenes de Trabajo" subtitle={params.toString() ? "Vista filtrada desde el dashboard." : "Una sola OT por trabajo. RR.HH., Operaciones, Abastecimiento y demás áreas trabajarán sobre estas OT."}
        actions={can("gerencia.ot.crear") && <Link to="/gerencia/ot/nueva"><Button data-testid="create-ot-btn" className="rounded-full"><Plus size={16} className="mr-1" />Crear OT</Button></Link>} />
      <div className="relative mb-5 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a9584]" />
        <Input data-testid="ots-search-input" className="pl-9 bg-white" placeholder="Buscar por número, título o proyecto" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
      </div>
      {data.items.length === 0 ? <div className="syc-card"><EmptyState title="Sin OT" text="Las OT se crean desde una venta adjudicada o directamente por Gerencia." /></div> : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">{data.items.map((o) => <OtCard key={o.id} o={o} />)}</div>
      )}
      <div className="syc-card mt-4"><Pager page={page} total={data.total} pageSize={data.page_size} onPage={setPage} /></div>
    </div>
  );
}
